import React, { useEffect, useState } from "react";
import { EcommProductPrice } from "@mindarc/arctheme-components/frontend/src/stories/components/ecommerce/Price/Price";
import ExtendedImage from "../../elements/ExtendedImage/ExtendedImage";
import helpers from '../../../../js/helpers';
import './ProductTile.scss';

declare const window: Window &
  typeof globalThis & {
    theme: {
      productBadges: any;
      productTileBadgesMetaobjects?: any[];
      ssSiteId?: string;
    };
  };

interface Swatch {
  color: string;
  image?: string;
  url: string;
}

export const ExtendedProductTile = ({
  product,
  badges,
  tog,
  product_swatches,
  show_price_inline = false,
  source = 'shopify',
  image_placeholder = null,
  view = 'product'
}) => {
  
  const [isHovered, setIsHovered] = useState(false);
  let imageIndex = 0;
  let hoverIndex = 1;

  const secondImageIsSwatch =
    product?.images?.[1]?.src?.includes("_s") ||
    product?.images?.[1]?.src?.includes("_s_");

  if (view === 'outfit') {
    imageIndex = 1;
    hoverIndex = 0;
  }

  if (product.media) {
    product.images = product.media;
  }

  const getImageUrl = (image) => {
    if (typeof image === "object" && image !== null && image.src) {
      return image.src;
    }
    return image;
  };

  // Product tags
  const createKeyValuePairs = (tags) => {
    const tagsArray = Array.isArray(tags) ? tags : tags ? [tags] : [];
    return tagsArray.reduce((acc, tag) => {
      if (typeof tag === 'string' && tag.includes('|')) {
        const [key, value] = tag.split("|");
        if (value) {
          acc[key] = acc[key] ? [...(Array.isArray(acc[key]) ? acc[key] : [acc[key]]), value] : value;
        }
      }
      return acc;
    }, {});
  };

  const productTags = createKeyValuePairs(product.tags);
  const promotionalBadge = productTags.promotional_badge ? [].concat(productTags.promotional_badge) : [];
  const hasBabyBountyAward = product.tags?.includes('2023 bounty baby awards') || false;
  const hasBadge = (badges?.metaobject_badge && badges?.metaobject_badge.image &&  badges?.metaobject_badge.label ) || (hasBabyBountyAward && badges?.bounty_baby_awards);
  const badgeImage = (badges?.metaobject_badge &&  badges?.metaobject_badge.image) || badges?.bounty_baby_awards;
  const badgelabel =  (badges?.metaobject_badge && badges?.metaobject_badge.label) || "2023 Baby Bounty Awards";

  const getBadgeColor = (badgeText) => {
    if (typeof badgeText === 'string') {
      let normalizedBadgeText = badgeText.toLowerCase().replace(/[^a-z0-9]/g, '_');

      if (normalizedBadgeText.includes('save')) {
        normalizedBadgeText = 'save';
      }

      const colorKey = `${normalizedBadgeText}_badge_color`;
      const color = window.theme.productBadges[colorKey];
      return color || '#323D3E';
    }
    return '#323D3E';
  };

  // Product Swatches
  let siblingSwatches: Swatch[] = [];

  if (source === 'shopify') {
    siblingSwatches = JSON.parse(product_swatches) as Swatch[];
  } else {
    siblingSwatches = product_swatches as Swatch[];
  }

  let additionalSwatchesCount = 0
  const maxVisibleSwatches = 4;

  if (siblingSwatches) {
    additionalSwatchesCount = siblingSwatches.length > maxVisibleSwatches ? siblingSwatches.length - maxVisibleSwatches : 0;
  }

  // Product Price
  const price = product.price;
  const compareAtPrice = product.compare_at_price;
  // @ts-ignore
  const currencySymbol = window.Alpine?.store('currency').getCurrencySymbol();
  const formattedPrice = `${currencySymbol}${source === 'shopify' ? price : helpers.formatPrice(price)}`;
  const formattedCompareAtPrice = `${currencySymbol}${source === 'shopify' ? compareAtPrice : helpers.formatPrice(compareAtPrice)}`;
  const renderBadges = () => (
    <div className="product-card__badge-list">
      {promotionalBadge.length > 0 && promotionalBadge.map((badgeText, index) => {
        const badgeColor = getBadgeColor(badgeText);
        return (
          <span
            key={index}
            className="badge"
            style={{ backgroundColor: badgeColor }}
          >
            {badgeText}
          </span>
        );
      })}
    </div>
  );

  // Custom Badges 
  const renderCustomProductBadges = () => {
    const customTags = Array.isArray(product.tags)
      ? product.tags.filter((tag) => typeof tag === "string" && tag.startsWith("custom|"))
      : [];

    if (!customTags.length) return null;

    const customHandles = customTags.map((tag) => tag.split("|")[1]);
    const metaBadges = window.theme?.productTileBadgesMetaobjects || [];
    let matchedBadges = metaBadges.filter((badge) =>
      customHandles.includes(badge.tag_handle)
    );

    // Limit total badges (promotional + custom) to 4
    const maxCustomBadges = 4 - promotionalBadge.length;
    if (maxCustomBadges > 0) {
      matchedBadges = matchedBadges.slice(0, maxCustomBadges);
    } else {
      matchedBadges = [];
    }

    if (!matchedBadges.length) return null;

    const badgesByPosition: { [key: string]: any[] } = {};
    matchedBadges.forEach((badge) => {
      const validPositions = ["Top Left", "Top Right", "Bottom Left", "Bottom Right"];
      let pos = validPositions.includes(badge.position) ? badge.position : "Top Left";

      if (!badgesByPosition[pos]) badgesByPosition[pos] = [];
      badgesByPosition[pos].push(badge);
    });

    // Discount calculation helpers
    const getDiscount = (badge) => {
      // Bundles
      if (product.bundlePrices) {
        const total = Number(product.bundlePrices.totalMax || product.bundlePrices.total || 0);
        const discounted = Number(product.bundlePrices.discountedMax || product.bundlePrices.discounted || 0);
        if (total > 0 && discounted < total) {
          return Math.round(((total - discounted) / total) * 100);
        }
      }
      // Regular products
      const compareAt = Number(product.compare_at_price || 0);
      const priceV = Number(product.price || 0);
      if (compareAt > priceV && compareAt > 0) {
        return Math.round(((compareAt - priceV) / compareAt) * 100);
      }
      return null;
    };

    const positionClassMap = {
      "top left": "badge--top-left",
      "top right": "badge--top-right",
      "bottom left": "badge--bottom-left",
      "bottom right": "badge--bottom-right",
    };

    const getFontSize = (badge) => {
      const addPx = (val) => {
        if (!val) return null;
        return val.toString().includes('px') ? val : `${val}px`;
      };

      const desktopSize = addPx(badge.desktop_font_size) || "12px";
      const mobileSize = addPx(badge.mobile_font_size) || desktopSize;

      return `clamp(${mobileSize}, 2vw, ${desktopSize})`;
    };

    return (
      <>
        {Object.entries(badgesByPosition).map(([pos, badges]) => (
          <div
            key={pos}
            className={`product-card__custom-badge ${positionClassMap[pos.toLowerCase()] || "badge--top-left"}`}
          >
            {badges.map((badge, i) => {
              let label = badge.tag_label || "";
              if (badge.tag_type === "Discount" && label.includes("%D")) {
                const disc = getDiscount(badge);
                if (disc !== null) label = label.replace("%D", `${disc}%`);
              }
              return (
                <span
                  key={badge.tag_handle || i}
                  className="badge badge--custom"
                  style={{
                    color: badge.font_colour || "#fff",
                    backgroundColor: badge.background_colour || "#323D3E",
                    borderRadius: badge.border_radius || "4px",
                    fontSize: getFontSize(badge)
                  }}
                >
                  {label}
                </span>
              );
            })}
          </div>
        ))}
      </>
    );
  };

  const renderImage = () => {
    const images = product.responsive_images
      ? product.responsive_images
      : product.images || [];

    // Fallback to Searchspring imageUrl or attributes.image if Shopify-style images are missing
    const imageToRender = isHovered && !secondImageIsSwatch
      ? images[hoverIndex] || { src: product.imageUrl || product.attributes?.image }
      : images[imageIndex] || { src: product.imageUrl || product.attributes?.image };

    const imageToRenderSet = product.responsive_images && (isHovered && !secondImageIsSwatch)
      ? images[hoverIndex]?.srcset || ''
      : images[imageIndex]?.srcset || '';

    return (
      <div className="product-card__figure">
        <a
          href={`/products/${product.handle}`}
          className="relative block pb-[120%]"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onMouseDown={() => window.IntelliSuggest?.clickItem?.(this, { siteId: window.theme.ssSiteId, intellisuggestData: product.intellisuggestData, intellisuggestSignature: product.intellisuggestSignature || product.is_signature })}
        >
          {imageToRender ? (
            <ExtendedImage
              classes="product-card__image"
              imageUrl={getImageUrl(imageToRender)}
              imageUrlSet={imageToRenderSet}
              alt={product.title || 'Product image'}
              isLazy={true}
            />
          ) : (
            image_placeholder && (
              <div className="absolute top-0 left-0 w-full h-full" dangerouslySetInnerHTML={{ __html: image_placeholder }} />
            )
          )}
          {hasBadge && (
            <ExtendedImage
              imageUrl={badgeImage}
              alt={badgelabel}
              isLazy={true}
              classes="absolute bottom-1 right-1 w-11 lg:w-[57px] lg:bottom-2.5 lg:right-2.5"
            />
          )}
        </a>
      </div>
    );
  };

  const renderTogBadges = () => {
    return (
      <div className="flex gap-1 lg:gap-2 min-h-[24px] flex-wrap">
        {tog && tog.length > 0 ? (
          tog.map((item, index) => (
            <span
              key={index}
              className="badge badge--tog"
              style={{ backgroundColor: item.tog_colour }}
            >
              {item.tog_title}
            </span>
          ))
        ) : (
          <span className="invisible badge badge--tog"> </span>
        )}
      </div>
    );
  };

  const renderProductTitle = () => (
    <span className="product-card__title min-h-[32px] lg:min-h-[48px]">
      <a
        href={`/products/${product?.handle}`}
        className="overflow-hidden hover:no-underline hover:text-highlight line-clamp-2"
      >
        {product?.title}
      </a>
    </span>
  )

  const renderPrice = () => {
    const formatPrice = (price) => {
      const numericPrice = Number(String(price).replace(/,/g, ""));
      return `${currencySymbol}${numericPrice.toFixed(2)}`;
    }

    if (product?.bundlePrices) {
      return (
        <div className="price">
          <span className="price price--compare">
            {!show_price_inline && Number(product?.bundlePrices.totalMax) == Number(product?.bundlePrices.totalMin) && (
              <div className="flex gap-1.5 flex-col">
                <span
                  className={`font-normal current-price text-[#BD5141]`}
                >
                 {formatPrice(product?.bundlePrices.discountedMax)}
                </span>
                {product?.compare_at_price > price && (
                  <span className="line-through compare-at-price price price--highlight">
                    {formatPrice(product?.bundlePrices.totalMax)}
                  </span>
                )}
              </div>
            )}
            {!show_price_inline  && Number(product?.bundlePrices.totalMax) != Number(product?.bundlePrices.totalMin) && (
              <div className="flex gap-1.5 flex-col">
                <span
                  className={`font-normal current-price text-[#BD5141]`}
                >
                  {formatPrice(product?.bundlePrices.discountedMin)} - {formatPrice(product?.bundlePrices.discountedMax)}
                </span>
                {product?.compare_at_price > price && (
                  <span className="line-through compare-at-price price price--highlight">
                     {formatPrice(product?.bundlePrices.totalMin)} - {formatPrice(product?.bundlePrices.totalMax)}
                  </span>
                )}
              </div>
            )}
          </span>
        </div>
      )
    }
  return (
    <div className="price">
      <span className="price price--compare">
        {!show_price_inline && (
          <div className={`flex ${
            product?.compare_at_price_min !== product?.compare_at_price_max
              ? 'flex-col lg:flex-row lg:gap-2.5'
              : 'gap-2.5'
          }`}>
            <span
              className={`font-normal current-price ${
                product?.compare_at_price > price ? 'text-[#BD5141]' : 'text-primary'
              }`}
            >
              {product?.price_min !== product?.price_max
                ? `${formatPrice(product?.price_min)} - ${formatPrice(product?.price_max)}`
                : formatPrice(product?.price_min)}
            </span>
            {product?.compare_at_price > price && (
              <span className="line-through compare-at-price price price--highlight">
                {product?.compare_at_price_min !== product?.compare_at_price_max
                  ? `${formatPrice(product?.compare_at_price_min)} - ${formatPrice(product?.compare_at_price_max)}`
                  : formatPrice(product?.compare_at_price_min)}
              </span>
            )}
          </div>
        )}
      </span>
    </div>
  );
}

  const renderSwatches = () => {
    if(product?.bundlePrices) return
    return (
      <div className="product-card__swatches min-h-[20px] lg:min-h-[28px]">
        <ul className="flex items-center p-0 m-0 list-none">
          {siblingSwatches?.slice(0, maxVisibleSwatches).map((swatch, index) => (
            <li
              key={index}
              className="inline-block mr-[2.5px] lg:mr-1.5"
            >
              <a
                href={swatch.url}
                className={`block overflow-hidden rounded-full w-5 h-5 lg:w-7 lg:h-7 ${index === 0 ? 'border border-black' : ''}`}
                style={{ backgroundColor: swatch.color }}
              >
                {swatch.image && (
                  <ExtendedImage
                    imageUrl={swatch.image}
                    alt="Swatch Image"
                    isLazy={true}
                    classes="w-full h-full object-cover"
                  />
                )}
              </a>
            </li>
          ))}
          {additionalSwatchesCount > 0 && (
            <li className="inline-block">
              <div className="flex items-center justify-center text-center text-primary font-body font-normal text-[10px]/[10px] ml-[2px] lg:text-base">
                + {additionalSwatchesCount}
              </div>
            </li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className="product-card w-full flex flex-col !p-0 hover:!transform-none hover:!shadow-none">
      <div className="flex flex-col justify-between flex-grow w-full">
        <div className="product-card__badges-stack">
          {renderBadges()}
        </div>
        <div className="relative">
          {renderCustomProductBadges()}
          {renderImage()}
        </div>
        <div className="flex flex-col justify-between flex-grow gap-2 product-card__info">
          {renderTogBadges()}
          {renderProductTitle()}
          {renderPrice()}
          {renderSwatches()}
        </div>
      </div>
    </div>
  );
}

export default ExtendedProductTile;
